import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Mail, Users, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Messages de contact
export function AdminContacts() {
  const { getAuthHeader } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${API}/contact`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-white py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <Link to="/admin/dashboard" className="hover:text-sand">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-xl font-bold">Messages reçus</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <p className="text-center text-forest/60">Chargement...</p>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <Mail className="w-12 h-12 text-forest/30 mx-auto mb-4" />
            <p className="font-body text-forest/60">Aucun message reçu</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => (
              <div 
                key={msg.id} 
                className="bg-white rounded-xl p-4 shadow-soft cursor-pointer hover:shadow-hover transition-shadow"
                onClick={() => setSelectedMessage(msg)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-heading font-semibold text-forest">{msg.name}</span>
                      <span className="text-xs text-forest/50">{msg.email}</span>
                    </div>
                    <p className="font-body font-medium text-autumn text-sm">{msg.subject}</p>
                    <p className="font-body text-sm text-forest/70 line-clamp-1 mt-1">{msg.message}</p>
                    <p className="font-body text-xs text-forest/40 mt-2">
                      {new Date(msg.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Message Detail Modal */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-forest">
              {selectedMessage?.subject}
            </DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="bg-sand/30 rounded-xl p-4">
                <p className="font-body font-semibold text-forest">{selectedMessage.name}</p>
                <p className="font-body text-sm text-forest/70">{selectedMessage.email}</p>
                {selectedMessage.phone && (
                  <p className="font-body text-sm text-forest/70">{selectedMessage.phone}</p>
                )}
              </div>
              <div className="font-body text-forest/80 whitespace-pre-line">
                {selectedMessage.message}
              </div>
              <div className="pt-4 border-t">
                <a
                  href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                  className="inline-block"
                >
                  <Button className="bg-forest hover:bg-forest-dark">
                    <Mail className="w-4 h-4 mr-2" />
                    Répondre par email
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Liste d'attente
export function AdminWaitlist() {
  const { getAuthHeader } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await fetch(`${API}/waitlist`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-white py-4 px-6">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <Link to="/admin/dashboard" className="hover:text-sand">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-xl font-bold">Liste d'attente</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <p className="text-center text-forest/60">Chargement...</p>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-forest/30 mx-auto mb-4" />
            <p className="font-body text-forest/60">Aucune inscription sur la liste d'attente</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
            <table className="w-full">
              <thead className="bg-sand/30">
                <tr>
                  <th className="text-left p-4 font-body font-semibold text-forest">Nom</th>
                  <th className="text-left p-4 font-body font-semibold text-forest">Email</th>
                  <th className="text-left p-4 font-body font-semibold text-forest">Téléphone</th>
                  <th className="text-left p-4 font-body font-semibold text-forest">Préférences</th>
                  <th className="text-left p-4 font-body font-semibold text-forest">Date</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} className="border-t border-sand/30">
                    <td className="p-4 font-body text-forest">{entry.name}</td>
                    <td className="p-4 font-body text-forest/70">
                      <a href={`mailto:${entry.email}`} className="hover:text-autumn">{entry.email}</a>
                    </td>
                    <td className="p-4 font-body text-forest/70">{entry.phone || '-'}</td>
                    <td className="p-4 font-body text-forest/70 text-sm max-w-xs truncate">
                      {entry.preferences || '-'}
                    </td>
                    <td className="p-4 font-body text-forest/50 text-sm">
                      {new Date(entry.created_at).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
